import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import { supabase } from '../../lib/supabase';
import {
  isOperatorFamilyRole,
  OPERATOR_PRIMARY_ROLE_PRIORITY,
  PRODUCTION_MANAGER_ROLE_TYPES,
  isProductionManagerRole,
} from '../../lib/operatorRoles';
import { ELEVATED_CASE_PRIORITIES } from '../../lib/casePriority';

import QuickActionCard from './components/QuickActionCard';
import ActivityItem from './components/ActivityItem';
import UrgentCaseCard from './components/UrgentCaseCard';
import RoleBasedMetrics from './components/RoleBasedMetrics';
import OverduePaymentAlerts from './components/OverduePaymentAlerts';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [currentLanguage, setCurrentLanguage] = useState('FR');
  const [userRoles, setUserRoles] = useState([]);
  const [primaryRole, setPrimaryRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [urgentCases, setUrgentCases] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [loadingUrgent, setLoadingUrgent] = useState(true);

  // Get current authenticated user and their roles
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setCurrentUserId(user.id);

          // Fetch user role assignments
          const { data: roleAssignments, error } = await supabase
            .from('user_role_assignments')
            .select(`
              id,
              role_id,
              country_id,
              is_active,
              user_roles (
                id,
                role_type,
                role_name
              )
            `)
            .eq('user_id', user.id)
            .eq('is_active', true);

          if (error) throw error;

          // Extract role types from active assignments
          const roles = (roleAssignments || [])
            .filter(ra => ra.user_roles)
            .map(ra => ra.user_roles.role_type);
          
          setUserRoles(roles);

          // Determine primary role (même priorité pour toute la famille opérateur)
          const rolePriority = [
            'administrator',
            'dentist_client',
            'dentist_supplier',
            ...PRODUCTION_MANAGER_ROLE_TYPES,
            ...OPERATOR_PRIMARY_ROLE_PRIORITY,
            'commercial',
            'distributor',
            'production_supplier',
          ];
          const primary = rolePriority.find(role => roles.includes(role)) || roles[0] || 'dentist_client';
          setPrimaryRole(primary);
          
          // Also store in localStorage for sidebar
          localStorage.setItem('userRole', primary);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('appLanguage');
    if (savedLanguage) setCurrentLanguage(savedLanguage);
  }, []);

  // Fetch urgent/high priority cases - FILTERED BY ROLE
  useEffect(() => {
    if (!currentUserId || !primaryRole) return;

    const fetchUrgentCases = async () => {
      try {
        setLoadingUrgent(true);
        
        // Build query based on role
        let query = supabase
          .from('cases')
          .select(`
            id,
            case_number,
            priority,
            due_date,
            status,
            user_profile_id,
            assigned_staff_id,
            prosthetist_id,
            patients (name),
            user_profiles!user_profile_id (full_name)
          `)
          .in('priority', ELEVATED_CASE_PRIORITIES)
          .not('status', 'in', '("completed","cancelled")')
          .order('due_date', { ascending: true })
          .limit(5);

        // Apply role-based filters
        if (primaryRole === 'dentist_client') {
          // Dentists see only their own cases
          query = query.eq('user_profile_id', currentUserId);
        } else if (isOperatorFamilyRole(primaryRole)) {
          query = query.eq('assigned_staff_id', currentUserId);
        } else if (primaryRole === 'dentist_supplier') {
          // Prosthetists/suppliers see cases assigned to them
          query = query.eq('prosthetist_id', currentUserId);
        } else if (isProductionManagerRole(primaryRole)) {
          query = query.in('status', ['in_production', 'quality_check', 'ready_for_delivery']);
        }
        // Administrator sees all cases (no filter)

        const { data, error } = await query;

        if (error) throw error;

        const mapped = (data || []).map((c) => ({
          id: c.id,
          caseNumber: c.case_number,
          patientName: c.patients?.name || 'N/A',
          priority: c.priority,
          dueDate: c.due_date
            ? new Date(c.due_date).toLocaleDateString('fr-FR')
            : 'N/A',
          dentist: c.user_profiles?.full_name || 'N/A',
          reason: getPriorityReason(c.priority, c.status),
        }));

        setUrgentCases(mapped);
      } catch (err) {
        console.error('Error fetching urgent cases:', err);
      } finally {
        setLoadingUrgent(false);
      }
    };

    fetchUrgentCases();
  }, [currentUserId, primaryRole]);

  // Fetch recent activities - FILTERED BY ROLE
  useEffect(() => {
    if (!currentUserId || !primaryRole) return;

    const fetchRecentActivities = async () => {
      try {
        setLoadingActivities(true);

        // Get accessible case IDs based on role
        let caseIds = [];
        
        if (primaryRole === 'dentist_client') {
          const { data: userCases } = await supabase
            .from('cases')
            .select('id')
            .eq('user_profile_id', currentUserId);
          caseIds = (userCases || []).map(c => c.id);
        } else if (isOperatorFamilyRole(primaryRole)) {
          const { data: staffCases } = await supabase
            .from('cases')
            .select('id')
            .eq('assigned_staff_id', currentUserId);
          caseIds = (staffCases || []).map(c => c.id);
        } else if (primaryRole === 'dentist_supplier') {
          const { data: prosthetistCases } = await supabase
            .from('cases')
            .select('id')
            .eq('prosthetist_id', currentUserId);
          caseIds = (prosthetistCases || []).map(c => c.id);
        } else if (isProductionManagerRole(primaryRole)) {
          const { data: productionCases } = await supabase
            .from('cases')
            .select('id')
            .in('status', ['in_production', 'quality_check', 'ready_for_delivery']);
          caseIds = (productionCases || []).map(c => c.id);
        }

        const activities = [];

        // Fetch cases
        let casesQuery = supabase
          .from('cases')
          .select(`
            id,
            case_number,
            status,
            created_at,
            user_profile_id,
            assigned_staff_id,
            prosthetist_id,
            user_profiles!user_profile_id (full_name),
            patients (name)
          `)
          .order('created_at', { ascending: false })
          .limit(3);

        // Apply filters based on role
        if (primaryRole === 'dentist_client') {
          casesQuery = casesQuery.eq('user_profile_id', currentUserId);
        } else if (isOperatorFamilyRole(primaryRole)) {
          casesQuery = casesQuery.eq('assigned_staff_id', currentUserId);
        } else if (primaryRole === 'dentist_supplier') {
          casesQuery = casesQuery.eq('prosthetist_id', currentUserId);
        } else if (isProductionManagerRole(primaryRole)) {
          casesQuery = casesQuery.in('status', ['in_production', 'quality_check', 'ready_for_delivery']);
        }

        const { data: casesData } = await casesQuery;

        // Map cases
        for (const c of casesData || []) {
          activities.push({
            id: `case-${c.id}`,
            type: 'case_created',
            title: 'Nouvelle conception créée',
            description: `Cas ${c.case_number} - Patient ${c.patients?.name || 'N/A'}`,
            timestamp: new Date(c.created_at),
            user: { 
              id: c.user_profile_id,
              name: c.user_profiles?.full_name || 'N/A' 
            },
          });
        }

        // Fetch messages only if we have accessible cases or user is admin
        if (caseIds.length > 0 || primaryRole === 'administrator') {
          let messagesQuery = supabase
            .from('case_messages')
            .select(`
              id,
              message_type,
              message_text,
              created_at,
              case_id,
              sender_id,
              user_profiles!sender_id (full_name)
            `)
            .order('created_at', { ascending: false })
            .limit(3);

          if (primaryRole !== 'administrator') {
            messagesQuery = messagesQuery.in('case_id', caseIds);
          }

          const { data: messagesData } = await messagesQuery;

          for (const msg of messagesData || []) {
            activities.push({
              id: `msg-${msg.id}`,
              type: 'message',
              title: 'Nouveau message',
              description: msg.message_text?.substring(0, 80) || '',
              timestamp: new Date(msg.created_at),
              user: { 
                id: msg.sender_id,
                name: msg.user_profiles?.full_name || 'N/A' 
              },
            });
          }
        }

        // Fetch incidents only if we have accessible cases or user is admin
        if (caseIds.length > 0 || primaryRole === 'administrator') {
          let incidentsQuery = supabase
            .from('case_incidents')
            .select(`
              id,
              title,
              severity,
              created_at,
              case_id,
              reported_by,
              user_profiles!reported_by (full_name)
            `)
            .order('created_at', { ascending: false })
            .limit(2);

          if (primaryRole !== 'administrator') {
            incidentsQuery = incidentsQuery.in('case_id', caseIds);
          }

          const { data: incidentsData } = await incidentsQuery;

          for (const inc of incidentsData || []) {
            activities.push({
              id: `inc-${inc.id}`,
              type: 'incident',
              title: 'Incident signalé',
              description: inc.title,
              timestamp: new Date(inc.created_at),
              user: { 
                id: inc.reported_by,
                name: inc.user_profiles?.full_name || 'N/A' 
              },
            });
          }
        }

        // Sort by timestamp descending and take top 5
        activities.sort((a, b) => b.timestamp - a.timestamp);
        setRecentActivities(activities.slice(0, 5));
      } catch (err) {
        console.error('Error fetching activities:', err);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchRecentActivities();
  }, [currentUserId, primaryRole]);

  const getPriorityReason = (priority, status) => {
    if (priority === 'urgent' || priority === 'urgente') {
      return 'Délai de livraison critique - Attention immédiate requise';
    }
    if ((priority === 'high' || priority === 'express') && status === 'in_review') {
      return 'En révision - Validation urgente requise';
    }
    if (priority === 'high' || priority === 'express') {
      return 'Priorité élevée - Traitement accéléré requis';
    }
    return '';
  };

  const getQuickActionsForRole = () => {
    if (isOperatorFamilyRole(primaryRole)) {
      return [
        { title: 'Suivi de production', description: 'Rechercher un lot — atelier en un clic', icon: 'Activity', iconColor: 'var(--color-primary)', onClick: () => navigate('/production-tracking-dashboard') },
        { title: 'Atelier de production', description: 'Scan lot, saisie cas, jalons atelier', icon: 'Wrench', iconColor: 'var(--color-accent)', onClick: () => navigate('/atelier-lot') },
        { title: 'Mes Cas Assignés', description: 'Cas à traiter', icon: 'Briefcase', iconColor: 'var(--color-success)', onClick: () => navigate('/cases-list') },
        { title: 'Formation', description: 'Ressources', icon: 'GraduationCap', iconColor: 'var(--color-warning)', onClick: () => navigate('/training-videos-library') },
      ];
    }
    switch (primaryRole) {
      case 'administrator':
        return [
          { title: 'Créer un Nouveau Cas', description: 'Démarrer un nouveau dossier patient', icon: 'Plus', iconColor: 'var(--color-primary)', onClick: () => navigate('/cases-list') },
          { title: 'Suivi de production', description: 'Vue globale, filtres dates et atelier', icon: 'Activity', iconColor: 'var(--color-accent)', onClick: () => navigate('/production-tracking-dashboard') },
          { title: 'Atelier de production', description: 'Scan lot et jalons atelier', icon: 'Wrench', iconColor: 'var(--color-success)', onClick: () => navigate('/atelier-lot') },
          { title: 'Gérer les Utilisateurs', description: 'Ajouter ou modifier les comptes', icon: 'UserCog', iconColor: 'var(--color-warning)', onClick: () => navigate('/admin-users-management') },
          { title: 'Voir les Factures', description: 'Consulter la facturation', icon: 'CreditCard', iconColor: 'var(--color-muted-foreground)', onClick: () => navigate('/dentist-invoices-tracking') },
        ];
    
      case 'dentist_client':
        return [
          { title: 'Créer un Cas', description: 'Nouveau dossier patient', icon: 'Plus', iconColor: 'var(--color-primary)', onClick: () => navigate('/cases-list') },
          { title: 'Espace dentiste', description: 'Accueil et priorités', icon: 'LayoutGrid', iconColor: 'var(--color-accent)', onClick: () => navigate('/dentiste') },
          { title: 'Cas Actifs', description: 'Suivre les traitements', icon: 'Briefcase', iconColor: 'var(--color-success)', onClick: () => navigate('/cases-list') },
          { title: 'Formation', description: 'Vidéos et ressources', icon: 'GraduationCap', iconColor: 'var(--color-warning)', onClick: () => navigate('/training-videos-library') },
        ];
      
      case 'dentist_supplier':
        return [
          { title: 'Voir les Cas', description: 'Accéder aux dossiers assignés', icon: 'Eye', iconColor: 'var(--color-primary)', onClick: () => navigate('/cases-list') },
          { title: 'Messages', description: 'Communications actives', icon: 'MessageSquare', iconColor: 'var(--color-accent)', onClick: () => navigate('/cases-list') },
          { title: 'Fichiers', description: 'Documents partagés', icon: 'FileText', iconColor: 'var(--color-success)', onClick: () => navigate('/cases-list') },
          { title: 'Formation', description: 'Ressources techniques', icon: 'GraduationCap', iconColor: 'var(--color-warning)', onClick: () => navigate('/training-videos-library') },
        ];
      
      default:
        if (isProductionManagerRole(primaryRole)) {
          return [
            { title: 'Suivi de production', description: 'Vue globale, filtres dates et atelier', icon: 'Activity', iconColor: 'var(--color-primary)', onClick: () => navigate('/production-tracking-dashboard') },
            { title: 'Atelier de production', description: 'Scan lot, saisie cas, jalons', icon: 'Wrench', iconColor: 'var(--color-accent)', onClick: () => navigate('/atelier-lot') },
            { title: 'Parc machines', description: 'Gestion du parc', icon: 'Cog', iconColor: 'var(--color-success)', onClick: () => navigate('/machine-fleet-management') },
            { title: 'Cas en production', description: 'Voir les cas actifs', icon: 'Briefcase', iconColor: 'var(--color-warning)', onClick: () => navigate('/cases-list') },
          ];
        }
        return [];
    }
  };

  const handleMetricClick = (path, filters = {}) => {
    if (path === '/cases-list' && filters.filter === 'messages') {
      navigate('/cases-list?messages=unread');
      return;
    }
    navigate(path, { state: filters });
  };

  const breadcrumbItems = [
    { label: 'Tableau de Bord', path: '/dashboard', icon: 'LayoutDashboard' },
  ];

  // Only show payment alerts for admin and dentist_client
  const showPaymentAlerts = primaryRole === 'administrator' || primaryRole === 'dentist_client';

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-2">Tableau de Bord</h1>
              <p className="text-muted-foreground">Vue d'ensemble de votre activité orthodontique</p>
            </div>
            <div className="text-sm text-muted-foreground">
              Dernière mise à jour:{' '}
              {new Date()?.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>

          <RoleBasedMetrics userRole={primaryRole} currentUserId={currentUserId} onMetricClick={handleMetricClick} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Actions Rapides</h2>
              </div>
              <div className="space-y-3">
                {getQuickActionsForRole()?.map((action, index) => (
                  <QuickActionCard key={index} {...action} />
                ))}
              </div>
            </div>

            {showPaymentAlerts && <OverduePaymentAlerts userRole={primaryRole} language={currentLanguage} />}
            
            {!showPaymentAlerts && (
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-foreground">Informations</h2>
                </div>
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    {isProductionManagerRole(primaryRole) && 'Consultez le suivi de production pour plus de détails'}
                    {isOperatorFamilyRole(primaryRole) && 'Consultez vos cas assignés pour commencer'}
                    {primaryRole === 'dentist_supplier' && 'Consultez vos cas assignés pour commencer'}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Urgent Cases */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Conceptions Urgentes</h2>
                {urgentCases?.length > 0 && (
                  <span className="notification-badge urgent">{urgentCases?.length}</span>
                )}
              </div>
              {loadingUrgent ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse bg-muted rounded-lg h-24"></div>
                  ))}
                </div>
              ) : urgentCases?.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun cas urgent en ce moment.</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {urgentCases?.map((caseData) => (
                    <UrgentCaseCard key={caseData?.id} caseData={caseData} />
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activities */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Activité Récente</h2>
                <button
                  onClick={() => navigate('/cases-list')}
                  className="text-sm text-primary hover:text-primary/80 font-medium transition-colors duration-200"
                >
                  Voir tout
                </button>
              </div>
              {loadingActivities ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse bg-muted rounded-lg h-16"></div>
                  ))}
                </div>
              ) : recentActivities?.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune activité récente.</p>
              ) : (
                <div className="space-y-2">
                  {recentActivities?.map((activity) => (
                    <ActivityItem 
                      key={activity?.id} 
                      activity={activity}
                      userRole={primaryRole}
                      currentUserId={currentUserId}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;